/**
 * VaultUploadTray — what is happening to the files you just dropped in.
 *
 * A batch upload used to be one anonymous progress bar. Drop twelve files in
 * and you were told only that something was in progress: not which file was
 * going up, not how many were done, and — worst — not that three of them had
 * been refused, because those arrived as toasts that faded while you were
 * watching the bar. A person could reasonably walk away believing all twelve
 * had landed.
 *
 * So the batch is shown as what it is: a list. Every file appears the moment it
 * is queued, each one says what became of it, and the header carries a running
 * count that keeps moving. Nothing about the outcome depends on having been
 * looking at the right moment — the list is still there afterwards, and it
 * closes when the person is done with it, not when the upload is.
 *
 * A file refused before it was ever sent (wrong type, too large) sits in this
 * same list with its reason. Being told "not this one, because…" beside the
 * eleven that worked is a different experience from a red toast with no
 * context.
 */

import React, { useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import './VaultUploadTray.css';

export type UploadStatus = 'queued' | 'uploading' | 'done' | 'replaced' | 'skipped' | 'failed';

export interface UploadItem {
  id: string;
  /** The file's own name. */
  name: string;
  /** Folders it arrived inside, when a whole folder was uploaded. '' at top level. */
  path: string;
  status: UploadStatus;
  /** Why it was skipped or failed — shown to the person, so it must read as a sentence. */
  reason?: string;
}

const Spinner = () => <span className="vut-spin" aria-hidden />;

const Tick = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
);

const Cross = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
);

const Chevron = ({ up }: { up: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
       style={{ transform: up ? 'rotate(180deg)' : undefined, transition: 'transform .15s' }}>
    <path d="M6 15l6-6 6 6" />
  </svg>
);

interface Props {
  items: UploadItem[];
  onClose: () => void;
}

export const VaultUploadTray: React.FC<Props> = ({ items, onClose }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(true);
  if (!items.length) return null;

  const settled = items.filter((i) => i.status !== 'queued' && i.status !== 'uploading');
  const landed = items.filter((i) => i.status === 'done' || i.status === 'replaced').length;
  const trouble = items.filter((i) => i.status === 'failed' || i.status === 'skipped').length;
  const running = settled.length < items.length;

  // While it runs, the count is the point — "4 of 12" answers "is this moving?".
  // Afterwards the useful sentence is what actually landed, and how much did
  // not, which is the part a progress bar could never say.
  const heading = running
    ? t('vault.upload.progress', { done: settled.length, total: items.length })
    : trouble
      ? t('vault.upload.doneWithTrouble', { done: landed, failed: trouble })
      : t('vault.upload.allDone', { n: landed });

  return (
    <section className="vut" role="status" aria-live="polite">
      <header className="vut__head">
        <span className="vut__title">{heading}</span>
        <button type="button" className="vut__icon" onClick={() => setOpen((v) => !v)}
                aria-label={t(open ? 'vault.upload.collapse' : 'vault.upload.expand')}>
          <Chevron up={!open} />
        </button>
        {/* Only once there is nothing left to watch: closing mid-flight would
            take away the only account of what is happening. */}
        {!running && (
          <button type="button" className="vut__icon" onClick={onClose}
                  aria-label={t('common.close')}>
            <Cross />
          </button>
        )}
      </header>

      {open && (
        <ul className="vut__list">
          {items.map((it) => (
            <li key={it.id} className={`vut__row is-${it.status}`}>
              <span className="vut__state">
                {it.status === 'uploading' && <Spinner />}
                {(it.status === 'done' || it.status === 'replaced') && <Tick />}
                {(it.status === 'failed' || it.status === 'skipped') && <Cross />}
              </span>
              <span dir="auto" className="vut__name" title={it.path ? `${it.path}/${it.name}` : it.name}>
                {it.path && <span className="vut__path">{it.path}/</span>}
                {it.name}
              </span>
              <span dir="auto" className="vut__note">
                {it.status === 'replaced' && t('vault.upload.replaced')}
                {(it.status === 'failed' || it.status === 'skipped') && it.reason}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default VaultUploadTray;
